import { SaveOutlined } from "@ant-design/icons";
import { Input, Row, Table } from "antd";
import { get, isEmpty, lowerCase, toLower } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Button from "../../../../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../../../../redux/ducks/Loading";
import ProjectInvoicePlan from "../../../../../../services/api/ProjectInvoicePlan";
import { mapMonths } from "../../../../../../utils/properties";
import { formatNumber, getClassName, showError, showSuccess, unformatNumber } from "../../../../../../utils/utilities";
import { refreshData } from "../../../../../../redux/ducks/BillingPlanManagement";

const ProductionTable = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const isRefreshData = useSelector((state) => state.billingPlanManagementDuck.refreshData);
  const params = useParams();
  const [state, setState] = useState({
    columnsProductionValue: [],
    customFormAdj: [],
    dataSource: [],
  });

  useEffect(() => {
    if (!isEmpty(props.allData) && !isEmpty(props.dataContentProduction)) {
      setState((prevState) => ({ ...prevState, dataSource: props.dataContentProduction }));
      columns(props.allData, props.dataContentProduction);
    }
    if (isRefreshData) {
      dispatch(refreshData(false));
    }
    if (tableRef.current && !isEmpty(props.allData)) {
      const columnNode = Array.from(tableRef.current.querySelectorAll("th")).filter(
        (th) => th.textContent == props.allData.openYear && th.classList.contains("ant-table-cell")
      );
      if (columnNode[0]) {
        tableRef.current.querySelector(".ant-table-content").scrollLeft = columnNode[0].offsetLeft;
      }
    }
  }, [props, tableRef.current]);

  const columns = (allData, trueData) => {
    let objCol = [
      {
        title: t("projectsManagement.planning.billingPlan.activityResource"),
        width: 200,
        fixed: "left",
        render: (record) => t(`projectsManagement.planning.billingPlan.${record.type}`),
      },
    ];

    trueData[0].yearValues.forEach((yv, i) => {
      let childrenArr = [];
      Object.keys(yv.columnValueMap).forEach((cvm) => {
        const timeMonth = getClassName(cvm, allData.openMonth, allData.openYear, yv.year);
        // Months
        childrenArr.push({
          title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
          width: 100,
          dataIndex: ["yearValues", i, "columnValueMap", cvm],
          className: `table-value-right ${
            showInput("displayType", timeMonth, userInfo.canEditAdjPlanning, "ADJUSTMENT", allData.closed) === "text"
              ? "past"
              : ""
          }`,
          render: (amount, record) =>
            formatNumber({
              name: cvm,
              value: amount ? amount : 0,
              lang: currentLanguage,
              id: cvm,
              displayType: showInput(
                "displayType",
                timeMonth,
                userInfo.canEditAdjPlanning,
                record.type,
                allData.closed
              ),
              decimalScale: showInput(
                "decimalScale",
                timeMonth,
                userInfo.canEditAdjPlanning,
                record.type,
                allData.closed
              ),
              suffix: "EUR",
              customInput: Input,
              onChange: (e) => handleChange(e, mapMonths[lowerCase(cvm)], yv.year),
            }),
        });
      });
      objCol.push({
        title: yv.year,
        width: 100,
        className: "table-value-right",
        children: childrenArr,
      });
    });

    // Total, total-1 total budget
    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT")}`),
      width: 100,
      dataIndex: ["totalValues", "TOT"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount, record) =>
        formatNumber({
          value: amount ? amount : 0,
          suffix: record.isHour ? "HOUR" : "EUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    });

    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_PREV_MONTH")}`),
      width: 100,
      dataIndex: ["totalValues", "TOT_PREV_MONTH"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount, record) =>
        formatNumber({
          value: amount ? amount : 0,
          suffix: record.isHour ? "HOUR" : "EUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    });

    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_BUDGET")}`),
      width: 100,
      dataIndex: ["totalValues", "TOT_BUDGET"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount, record) =>
        formatNumber({
          value: amount ? amount : 0,
          suffix: record.isHour ? "HOUR" : "EUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    });

    setState((prevState) => ({ ...prevState, columnsProductionValue: objCol }));
  };

  const showInput = (type, timeMonth, canEdit, activity, closed) => {
    let displayType = "text";
    let decimalScale = 0;
    if (
      (timeMonth === "future" && canEdit && activity === "ADJUSTMENT" && projectManagement.enabled) ||
      (timeMonth === "present" && canEdit && activity === "ADJUSTMENT" && projectManagement.enabled && !closed)
    ) {
      displayType = "input";
      decimalScale = 2;
    }
    return type === "displayType" ? displayType : decimalScale;
  };

  const handleChange = (e, month, year) => {
    let customForm = state.customFormAdj;
    const amount = unformatNumber(e.target.value, currentLanguage, "amount");

    const newDataSource = [...state.dataSource];
    const changedTableRowIndex = newDataSource.findIndex((row) => row.type === "ADJUSTMENT");
    const changedYearIndex = newDataSource[changedTableRowIndex].yearValues.findIndex((yv) => yv.year === year);
    if (isEmpty(customForm)) {
      customForm.push({ year, month, amount });
    } else {
      const foundIndex = customForm.findIndex((item) => item.year === year && item.month === month);
      if (foundIndex !== -1) {
        customForm[foundIndex].amount = amount;
      } else {
        customForm.push({ year, month, amount });
      }
    }
    // Update table dataSource values
    for (let cv in newDataSource[changedTableRowIndex].yearValues[changedYearIndex].columnValueMap) {
      if (mapMonths[lowerCase(cv)] === month) {
        newDataSource[changedTableRowIndex].yearValues[changedYearIndex].columnValueMap[cv] = amount;
      }
    }
    setState((prevState) => ({ ...prevState, customFormAdj: customForm, dataSource: newDataSource }));
  };

  const handleSaveProduction = async () => {
    dispatch(setLoading(true));
    const response = await ProjectInvoicePlan.saveDataProjectProductionPlan(params.id, state.customFormAdj);
    if (!response.error) {
      showSuccess();
      setState((prevState) => ({ ...prevState, customFormAdj: [] }));
      dispatch(refreshData(true));
    } else {
      showError(get(response, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  return (
    <>
      <div ref={tableRef}>
        <Table
          bordered
          dataSource={state.dataSource}
          columns={state.columnsProductionValue}
          size="small"
          pagination={false}
          scroll={{ x: 1500 }}
        />
      </div>
      <Row type="flex" align="middle" justify="end" className="mb-10 mt-10">
        <Button
          isDisabled={!(userInfo.canEditAdjPlanning && !isEmpty(state.customFormAdj) && projectManagement.enabled)}
          currentIcon={<SaveOutlined className="icon-medium" />}
          className="ml-10"
          clickCallback={handleSaveProduction}
        />
      </Row>
    </>
  );
};

export default ProductionTable;
